/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useState } from 'react'

import {
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHorizontalRule,
  EuiLink,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiSpacer,
} from '@elastic/eui'

import type { AnyObservabilityProject } from '@modules/project-api/types'
import genericMessages from '@modules/project-lib/genericMessages'
import { FormattedMessage } from '@modules/i18n'
import SelectObservabilityTier from '@modules/project-creation-components/SelectObservabilityTier'

import type { AnyObservabilityProjectProductTier } from './types'

export type Props = {
  project: AnyObservabilityProject
  onClose: () => void
  onChangeSave: (
    productTypes: AnyObservabilityProject['product_tier'],
    onSettled: () => void,
  ) => void
  isLoading?: boolean
}

const DEFAULT_O11Y_TIER: AnyObservabilityProjectProductTier = 'complete'

const ObservabilityProjectFeaturesModal: React.FunctionComponent<Props> = ({
  project,
  onClose,
  onChangeSave,
  isLoading,
}) => {
  const [productTier, setProductTier] = useState<AnyObservabilityProjectProductTier>(
    project.product_tier || DEFAULT_O11Y_TIER,
  )

  const getDisabledTiers = (
    currentTier?: AnyObservabilityProjectProductTier,
  ): AnyObservabilityProjectProductTier[] => {
    if (currentTier === 'complete') {
      return ['logs_essentials']
    }

    return []
  }

  const close = () => {
    setProductTier(project.product_tier || DEFAULT_O11Y_TIER)
    onClose()
  }

  return (
    <EuiModal onClose={close}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>
          <FormattedMessage
            id='project.observabilityProjectFeatures.title'
            defaultMessage='Project features: {name}'
            values={{ name: project.name }}
          />
        </EuiModalHeaderTitle>
      </EuiModalHeader>

      <EuiModalBody className='.eui-yScroll'>
        <EuiHorizontalRule margin='none' />
        <EuiSpacer size='m' />
        <SelectObservabilityTier
          value={productTier}
          onChange={setProductTier}
          hideTitle={true}
          disabledTiers={getDisabledTiers(project.product_tier)}
        />
      </EuiModalBody>
      <EuiModalFooter>
        <EuiFlexGroup direction='column'>
          <EuiFlexItem grow={false}>
            <EuiCallOut
              title={
                <FormattedMessage
                  id='project.observabilityProjectFeatures.downgradeWarning'
                  defaultMessage='Changing your product feature selection from Observability Logs Essentials to Observability Complete is a permanent action and cannot be reversed.'
                />
              }
              color='warning'
              size='s'
              iconType='alert'
            />
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiFlexGroup gutterSize='m' justifyContent='spaceBetween' alignItems='baseline'>
              <EuiFlexItem grow={false}>
                <EuiLink
                  href='https://www.elastic.co/pricing/serverless-observability'
                  rel='noopener noreferrer'
                  target='_blank'
                >
                  <FormattedMessage
                    id='projects.messages.comparison'
                    defaultMessage='View full plan comparison'
                  />
                </EuiLink>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                {project.product_tier === 'complete' ? (
                  <EuiButton
                    size='s'
                    color='primary'
                    fill={true}
                    isLoading={isLoading}
                    onClick={close}
                    minWidth='6rem'
                  >
                    <FormattedMessage {...genericMessages.close} />
                  </EuiButton>
                ) : (
                  <EuiFlexGroup justifyContent='flexEnd'>
                    <EuiFlexItem grow={false}>
                      <EuiButtonEmpty size='s' onClick={close}>
                        <FormattedMessage {...genericMessages.cancel} />
                      </EuiButtonEmpty>
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                      <EuiButton
                        size='s'
                        color='primary'
                        fill={true}
                        isLoading={isLoading}
                        onClick={() => {
                          onChangeSave(productTier, onClose)
                        }}
                        minWidth='6rem'
                      >
                        <FormattedMessage {...genericMessages.save} />
                      </EuiButton>
                    </EuiFlexItem>
                  </EuiFlexGroup>
                )}
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiModalFooter>
    </EuiModal>
  )
}

export default ObservabilityProjectFeaturesModal
